import client from '@/shared/api/client';
import { isMockMode } from '@/__dev__/isMockMode';
import { isFederatedAccessNotGranted } from '@/shared/utils/apiError';
import {
  mockLogin,
  mockValidateMFA,
  mockGetUserInfo,
  mockResendMFA,
  mockRequestResetPassword,
  mockResetPassword,
  mockChangeBusinessAccount,
  mockCheckFederation,
} from '@/__dev__/mocks/auth.mock';
import type {
  LoginRequest,
  LoginResponse,
  MFAValidateRequest,
  MFAValidateResponse,
  UserInfoResponse,
  MFAResendRequest,
  MFAResendResponse,
  ResetPasswordRequestRequest,
  ResetPasswordRequestResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangeBusinessAccountRequest,
  ChangeBusinessAccountResponse,
  FederationCheckRequest,
  FederationCheckResponse,
} from '@/features/auth/types/auth.types';

/**
 * Authenticates user with email and password
 * Returns user data for MFA step (no token yet)
 */
export async function login(
  payload: LoginRequest
): Promise<{ data: LoginResponse; status: number }> {
  if (isMockMode) return mockLogin(payload);

  const response = await client.post<LoginResponse>('/auth/login', payload);
  return { data: response.data, status: response.status };
}

/**
 * Validates MFA code and completes authentication
 * Returns JWT token and user data
 */
export async function validateMFA(
  payload: MFAValidateRequest
): Promise<MFAValidateResponse> {
  if (isMockMode) return mockValidateMFA();
  const { data } = await client.post<MFAValidateResponse>(
    '/auth/fulfill-mfa',
    payload
  );
  return data;
}

/**
 * Fetches authenticated user information.
 *
 * @param accessToken - Token to use instead of the stored session (a freshly
 *   issued token not yet persisted)
 */
export async function getUserInfo(
  accessToken?: string
): Promise<UserInfoResponse> {
  if (isMockMode) return mockGetUserInfo();
  const { data } = await client.get<UserInfoResponse>(
    '/auth/user-info',
    accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined
  );
  return data;
}

/**
 * Checks authorization with a freshly issued token, before it is stored and
 * the session is marked as authenticated in the app. Only the explicit
 * no-platform-access response blocks login; transient failures do not get
 * misreported as an authorization denial.
 *
 * On denial the token is simply discarded by the caller, so no usable
 * session is left behind.
 */
export async function hasPlatformAccess(accessToken: string): Promise<boolean> {
  try {
    await getUserInfo(accessToken);
    return true;
  } catch (error) {
    return !isFederatedAccessNotGranted(error);
  }
}

/**
 * Resends MFA token to user
 * Requests a new MFA code to be sent using the session ID
 */
export async function resendMFA(
  payload: MFAResendRequest
): Promise<MFAResendResponse> {
  if (isMockMode) return mockResendMFA();
  const { data } = await client.post<MFAResendResponse>(
    '/auth/leverage-identity-mfa',
    payload
  );
  return data;
}

/**
 * Requests password reset by sending OTP token to user's email
 * First step of the password reset flow
 *
 * @param payload - Contains user's email address
 * @returns Promise with success confirmation message
 *
 * @example
 * ```typescript
 * const response = await requestResetPassword({ email: 'user@example.com' });
 * // OTP sent to email
 * ```
 */
export async function requestResetPassword(
  payload: ResetPasswordRequestRequest
): Promise<ResetPasswordRequestResponse> {
  if (isMockMode) return mockRequestResetPassword(payload);
  const { data } = await client.post<ResetPasswordRequestResponse>(
    '/auth/reset-password/request',
    payload
  );
  return data;
}

/**
 * Resets user password using OTP token received by email
 * Second step of the password reset flow
 *
 * @param payload - Contains email, new password, and OTP token
 * @returns Promise with success confirmation message
 *
 * @example
 * ```typescript
 * const response = await resetPassword({
 *   email: 'user@example.com',
 *   newPassword: 'NewSecurePass123!',
 *   otp: '123456'
 * });
 * // Password successfully changed
 * ```
 */
export async function resetPassword(
  payload: ResetPasswordRequest
): Promise<ResetPasswordResponse> {
  if (isMockMode) return mockResetPassword();
  const { data } = await client.post<ResetPasswordResponse>(
    '/auth/reset-password',
    payload
  );
  return data;
}

/**
 * Checks whether the e-mail domain belongs to a federated (SSO) organization
 *
 * Public endpoint used by the "Acessar com SSO" screen. When the domain is
 * federated, the response carries the IAM-SAML `registrationId` (and the full
 * `authenticationUrl` when the backend has it configured) used to start the
 * SAML flow at the organization's IdP.
 *
 * @param payload - Contains the corporate e-mail address
 * @returns Promise with the federation check result
 *
 * @example
 * ```typescript
 * const result = await checkFederation({ email: 'user@bancobv.com.br' });
 * if (result.federated) window.location.assign(result.authenticationUrl!);
 * ```
 */
export async function checkFederation(
  payload: FederationCheckRequest
): Promise<FederationCheckResponse> {
  if (isMockMode) return mockCheckFederation(payload);
  const { data } = await client.post<FederationCheckResponse>(
    '/auth/federation/check',
    payload
  );
  return data;
}

/**
 * Changes the business account context for the logged user
 *
 * Returns a new token with the updated businessId context.
 *
 * @param payload - Contains the new businessId
 * @returns Promise with new access token and user data
 *
 * @example
 * ```typescript
 * const response = await changeBusinessAccount({ businessId: 'new-business-123' });
 * // Store new token and refresh user context
 * setAuthSession(response);
 * ```
 */
export async function changeBusinessAccount(
  payload: ChangeBusinessAccountRequest
): Promise<ChangeBusinessAccountResponse> {
  if (isMockMode) return mockChangeBusinessAccount();
  const { data } = await client.post<ChangeBusinessAccountResponse>(
    `/business-account/change-account/${payload.businessId}`
  );
  return data;
}
